import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ArticleCard from "@/components/feature/ArticleCard";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta, { useJsonLd } from "@/hooks/usePageMeta";
import { categories, articles } from "@/mocks/articles";

export default function Category() {
  const { cat = "star" } = useParams();
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const current =
    categories.find((c) => c.key === cat) || categories.find((c) => c.key === "star")!;

  const list = useMemo(
    () => articles.filter((a) => a.catKey === current.key),
    [current.key]
  );

  const allTags = useMemo(() => Array.from(new Set(list.map((a) => a.tags).flat())), [list]);

  const filtered = activeTag ? list.filter((a) => a.tags.includes(activeTag)) : list;

  usePageMeta({
    title: `${current.name}_${current.desc}_每日黑料公众人物盘点`,
    description: `每日黑料「${current.name}」栏目持续收录${current.desc}，谁被翻旧账、谁出新瓜，${current.name}深度盘点一网打尽，看${current.name}就从这里入口。`,
    keywords: `每日黑料,${current.name},${current.name}盘点,每日黑料人物,公众人物盘点`,
    canonicalPath: `/category/${current.key}`,
  });

  useJsonLd([
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${current.name} - 每日黑料`,
      description: current.desc,
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: "每日黑料" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "栏目盘点" },
        { "@type": "ListItem", position: 3, name: current.name },
      ],
    },
  ]);

  return (
    <>
      {/* 面包屑 */}
      <div className="border-b border-foreground-900/10 bg-background-50">
        <nav className="mx-auto flex max-w-6xl items-center gap-1.5 px-4 py-2.5 text-xs text-foreground-600 md:px-6" aria-label="面包屑">
          <Link to="/" className="flex items-center gap-1 hover:text-primary-700">
            <i className="ri-home-5-line"></i>首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span>栏目盘点</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="font-medium text-primary-700">{current.name}</span>
        </nav>
      </div>

      {/* 栏目头图 */}
      <section className="border-b border-foreground-900/10 bg-foreground-950 text-background-100">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-14">
          <p className="font-label text-sm tracking-[0.35em] text-accent-400">{current.en}</p>
          <h1 className="mt-2 font-heading text-4xl font-black text-background-50 md:text-5xl">
            {current.name}
            <span className="ml-3 align-middle text-base font-medium text-background-300">
              {current.en.split(" ")[0]} / 盘点单元
            </span>
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-7 text-background-200">{current.desc}</p>

          {/* 栏目切换 */}
          <div className="mt-7 flex flex-wrap gap-2">
            {categories.map((c) => {
              const active = c.key === current.key;
              return (
                <Link
                  key={c.key}
                  to={`/category/${c.key}`}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${
                    active
                      ? "border-accent-500 bg-accent-500 font-bold text-foreground-950"
                      : "border-background-200/40 text-background-200 hover:border-accent-400 hover:text-accent-300"
                  }`}
                >
                  {c.name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14" aria-label={`${current.name}内容`}>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          {/* 文章区 */}
          <div className="lg:col-span-2">
            {/* 标签筛选 */}
            {allTags.length > 0 && (
              <div className="mb-6 flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-600">按标签筛选：</span>
                <button
                  type="button"
                  onClick={() => setActiveTag(null)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                    !activeTag ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  全部 {list.length}
                </button>
                {allTags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setActiveTag(activeTag === t ? null : t)}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                      activeTag === t ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {filtered.map((a) => (
                  <ArticleCard key={a.slug} article={a} layout="grid" showLead />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-foreground-900/10 bg-background-100/60 p-12 text-center">
                <i className="ri-inbox-2-line text-4xl text-foreground-400"></i>
                <p className="mt-3 text-foreground-600">该标签下暂无内容，看看其他栏目吧。</p>
              </div>
            )}
          </div>

          {/* 侧栏 */}
          <aside className="space-y-8">
            <div className="rounded-lg border border-foreground-900/10 bg-background-100/60 p-5">
              <h3 className="font-heading text-lg font-black text-foreground-950">栏目说明</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-600">{current.desc}</p>
              <div className="mt-4 flex items-center gap-2 rounded-md bg-background-50 px-3 py-2 text-xs text-foreground-600">
                <i className="ri-shield-check-line text-primary-600"></i>
                栏目内信息均标注来源与核实状态
              </div>
            </div>

            <div className="rounded-lg border border-foreground-900/10 bg-background-100/60 p-5">
              <h3 className="font-heading text-lg font-black text-foreground-950">其他栏目</h3>
              <ul className="mt-3 space-y-3">
                {categories
                  .filter((c) => c.key !== current.key)
                  .map((c) => (
                    <li key={c.key}>
                      <Link to={`/category/${c.key}`} className="group flex items-center gap-3">
                        <img
                          src={c.image}
                          alt={c.name}
                          title={`${c.name} 栏目`}
                          className="h-12 w-12 rounded-md object-cover object-top"
                        />
                        <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                          {c.name}
                        </span>
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}